import type { ComboboxAbsenceType, FormDialogRef } from '@/components'
import {
  AbsenceTypeCombobox,
  Button,
  DatePicker,
  FAIcon,
  Field,
  FieldGroup,
  FieldLabel,
  FormDialog,
  Textarea,
  TimePicker,
  UserCombobox,
} from '@/components'
import { cn } from '@/lib/utils'
import UserThumbOrInitials from '@/modules/users/UserThumbOrInitials'
import { combineDateAndTime, maxDate, useFormWatch, useUser } from '@/utils'
import type { ApolloError } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addDays, addHours, startOfDay, startOfHour } from 'date-fns'
import { Activity, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { Controller, useForm } from 'react-hook-form'
import z from 'zod'
import { CREATE_ABSENCE_MUTATION, PATCH_ABSENCE_MUTATION } from '../mutations'
import { ABSENCE_TYPES_COMBOBOX_QUERY } from '../queries'
import { AbsencePeriodReport } from './absence-period-report'
import { MultiFileUpload } from './multi-file-upload'

export const AbsenceFileSchema = z.file().or(
  z.object({
    id: z.string(),
    name: z.string(),
    url: z.string(),
  })
)

export type AbsenceFile = z.infer<typeof AbsenceFileSchema>

function useAbsenceSchema() {
  const translations = useTranslate({
    userRequired: 'validation.field-required',
    absenceTypeRequired: 'validation.field-required',
  })

  return z.object({
    id: z.string(),
    user: z.string().min(1, translations.userRequired),
    absenceType: z.string().min(1, translations.absenceTypeRequired),
    datetimeStart: z.date(),
    datetimeEnd: z.date(),
    reason: z.string(),
    files: AbsenceFileSchema.array(),
  })
}

export type AbsenceForm = z.infer<ReturnType<typeof useAbsenceSchema>>

function transformAbsenceDates(
  values: AbsenceForm,
  selectedAbsenceType: ComboboxAbsenceType
) {
  const datetimeStart = (
    selectedAbsenceType.durationMode === 'HOURLY'
      ? values.datetimeStart
      : startOfDay(values.datetimeStart)
  ).toISOString()

  const datetimeEnd = (
    selectedAbsenceType.durationMode === 'HOURLY'
      ? values.datetimeEnd
      : startOfDay(addDays(values.datetimeEnd, 1))
  ).toISOString()

  return {
    datetimeStart,
    datetimeEnd,
  }
}

type Absence = AbsenceForm & { id: string }

export interface AbsenceDialogRef {
  open(absence: Absence): void
}

interface Props {
  ref?: React.RefObject<AbsenceDialogRef | null>

  canAddForOthers: boolean
  createLockedBefore: Date | null
  hideTrigger?: boolean
}

export function AbsenceDialog({
  ref,

  canAddForOthers,
  createLockedBefore,
  hideTrigger = false,
}: Props) {
  const translations = useTranslate({
    headerAdd: 'absences.absence-dialog.header-add',
    headerEdit: 'absences.absence-dialog.header-edit',
    add: 'absences.absence-dialog.add',

    overlapError: 'absences.absence-dialog.errors.overlap',

    labels: {
      user: 'absences.absence-dialog.labels.user',
      absenceType: 'absences.absence-dialog.labels.absence-type',
      timeFrom: 'absences.absence-dialog.labels.time-from',
      timeTo: 'absences.absence-dialog.labels.time-to',
      dateFrom: 'absences.absence-dialog.labels.date-from',
      dateTo: 'absences.absence-dialog.labels.date-to',
      reason: 'absences.absence-dialog.labels.reason',
      attachments: ['absences.absence-dialog.labels.attachments', { n: 0 }],
    },
  })

  const dialogRef = useRef<FormDialogRef>(null)
  const originalFilesRef = useRef<Set<string>>(new Set())

  const me = useUser()
  const [reportValid, setReportValid] = useState(true)
  const [overlapError, setOverlapError] = useState(false)

  const { data: absenceTypesData } = useQuery(ABSENCE_TYPES_COMBOBOX_QUERY)

  const now = useMemo(() => new Date(), [])
  const dtStart = startOfHour(
    maxDate(now, combineDateAndTime(createLockedBefore ?? now, now))
  )
  const form = useForm<AbsenceForm>({
    resolver: zodResolver(useAbsenceSchema()),
    defaultValues: {
      id: '',
      user: canAddForOthers ? '' : me.id,
      absenceType: '',
      datetimeStart: dtStart,
      datetimeEnd: addHours(dtStart, 1),
      reason: '',
      files: [],
    },
    mode: 'onBlur',
  })

  useImperativeHandle(ref, () => ({
    open(absence: Absence) {
      dialogRef.current?.open()
      form.reset(absence)

      const originalFileIds = new Set<string>()
      for (const file of absence.files) {
        if (!(file instanceof File)) originalFileIds.add(file.id)
      }
      originalFilesRef.current = originalFileIds
    },
  }))

  const absenceId = useFormWatch(form, 'id')

  const absenceTypeId = useFormWatch(form, 'absenceType')
  const selectedAbsenceType =
    absenceTypesData?.allAbsenceTypes.find(type => type.id === absenceTypeId) ??
    null

  const userId = useFormWatch(form, 'user')
  const datetimeStart = useFormWatch(form, 'datetimeStart')
  const datetimeEnd = useFormWatch(form, 'datetimeEnd')
  const files = useFormWatch(form, 'files')

  const showTimePicker = selectedAbsenceType?.durationMode === 'HOURLY'

  function checkApolloError(error: ApolloError) {
    setOverlapError(
      error.message.includes('This absence overlaps an existing absence.')
    )
  }

  const [createAbsence] = useMutation(CREATE_ABSENCE_MUTATION, {
    refetchQueries: [
      'UserAbsences',
      'AbsenceRequests',
      'AbsenceDetails',
      'Sidebar',
    ],
    awaitRefetchQueries: true,
    onCompleted() {
      setOverlapError(false)
      dialogRef.current?.close()
    },
    onError: checkApolloError,
  })

  const [patchAbsence] = useMutation(PATCH_ABSENCE_MUTATION, {
    refetchQueries: [
      'UserAbsences',
      'AbsenceRequests',
      'AbsenceDetails',
      'Sidebar',
    ],
    awaitRefetchQueries: true,
    onCompleted() {
      dialogRef.current?.close()
      setOverlapError(false)
    },
    onError: checkApolloError,
  })

  const confirmDisabled =
    !selectedAbsenceType ||
    !reportValid ||
    overlapError ||
    datetimeStart >= datetimeEnd

  function userCanHaveSickChildAbsence(userId: string) {
    const user = absenceTypesData?.allUsersList.find(u => u.id === userId)
    if (!user) return false
    return !!user.childcare.numberOfChildren
  }

  function resetForm() {
    setTimeout(() => {
      setOverlapError(false)
      form.reset()
    }, 300)
  }

  function handleChangeDate(
    field: 'start' | 'end',
    onChange: (...args: any[]) => void
  ) {
    return (date: Date | null) => {
      if (!date) return

      setOverlapError(false)
      onChange(date)

      if (!selectedAbsenceType) return

      if (field === 'start') {
        const currentEnd = form.getValues('datetimeEnd')
        if (date >= currentEnd) {
          const newEnd = addHours(startOfHour(date), 1)
          form.setValue('datetimeEnd', newEnd)
        }
      } else {
        const currentStart = form.getValues('datetimeStart')
        if (date <= currentStart) {
          const newStart = startOfHour(date)
          form.setValue('datetimeStart', newStart)
        }
      }
    }
  }

  function categorizeFiles(files: AbsenceForm['files']) {
    const filesAdd: { file: File }[] = []

    const fileIds = new Set<string>()
    for (const file of files) {
      if (file instanceof File) filesAdd.push({ file })
      else fileIds.add(file.id)
    }

    const filesRemove = Array.from(originalFilesRef.current.difference(fileIds))

    return { filesAdd, filesRemove }
  }

  async function handleSubmit(values: AbsenceForm) {
    if (!selectedAbsenceType) return

    const { filesAdd, filesRemove } = categorizeFiles(values.files)

    if (!values.id)
      await createAbsence({
        variables: {
          input: {
            user: values.user,
            absenceType: values.absenceType,
            reason: values.reason,
            filesAdd,
            ...transformAbsenceDates(values, selectedAbsenceType),
          },
        },
      })
    else
      await patchAbsence({
        variables: {
          id: values.id,
          input: {
            user: values.user,
            absenceType: values.absenceType,
            reason: values.reason,
            filesAdd,
            filesRemove,
            ...transformAbsenceDates(values, selectedAbsenceType),
          },
        },
      })
  }

  return (
    <FormDialog
      ref={dialogRef}
      form={form}
      trigger={
        <Button className={cn(hideTrigger && 'hidden')} size="sm">
          <FAIcon icon="plus" />
          <span>{translations.add}</span>
        </Button>
      }
      header={!absenceId ? translations.headerAdd : translations.headerEdit}
      noCloseOnEscape
      confirmDisabled={confirmDisabled}
      error={overlapError && translations.overlapError}
      onClose={resetForm}
      onSubmit={handleSubmit}
    >
      <FieldGroup>
        <Activity mode={canAddForOthers ? 'visible' : 'hidden'}>
          <Controller
            name="user"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel>{translations.labels.user}</FieldLabel>

                <UserCombobox
                  {...field}
                  mode="single"
                  alwaysInclude={me}
                  onChange={value => {
                    if (
                      selectedAbsenceType?.preset === 'SICK_CHILD_NO' &&
                      !userCanHaveSickChildAbsence(value)
                    )
                      form.setValue('absenceType', '')

                    field.onChange(value)
                  }}
                />
              </Field>
            )}
          />
        </Activity>

        <Activity mode={!canAddForOthers ? 'visible' : 'hidden'}>
          <Field>
            <FieldLabel>{translations.labels.user}</FieldLabel>

            <div className="flex items-center gap-4 px-6 py-3 rounded-xl border border-gray-200 bg-quaternary">
              <UserThumbOrInitials user={me} />

              <span className="text-lg font-semibold">{me.fullName}</span>
            </div>
          </Field>
        </Activity>

        <Controller
          name="absenceType"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.absenceType}</FieldLabel>

              <AbsenceTypeCombobox
                {...field}
                mode="single"
                hideItem={item => {
                  if (item.preset !== 'SICK_CHILD_NO') return false
                  return !userCanHaveSickChildAbsence(userId)
                }}
              />
            </Field>
          )}
        />

        <Activity
          mode={selectedAbsenceType?.preset !== 'SIMPLE' ? 'visible' : 'hidden'}
        >
          <AbsencePeriodReport
            form={form}
            durationMode={selectedAbsenceType?.durationMode}
            onValidate={setReportValid}
          />
        </Activity>

        <Activity mode={!!selectedAbsenceType ? 'visible' : 'hidden'}>
          <Controller
            name="datetimeStart"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel>
                  {showTimePicker
                    ? translations.labels.timeFrom
                    : translations.labels.dateFrom}
                </FieldLabel>

                <div className="flex items-end gap-2">
                  {showTimePicker && <TimePicker {...field} />}

                  <DatePicker
                    {...field}
                    className="w-full"
                    mode="single"
                    disabledDates={
                      !!createLockedBefore
                        ? {
                            before: createLockedBefore,
                          }
                        : undefined
                    }
                    onChange={handleChangeDate('start', field.onChange)}
                  />
                </div>
              </Field>
            )}
          />

          <Controller
            name="datetimeEnd"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel>
                  {showTimePicker
                    ? translations.labels.timeTo
                    : translations.labels.dateTo}
                </FieldLabel>

                <div className="flex items-end gap-2">
                  {showTimePicker && <TimePicker {...field} />}

                  <DatePicker
                    {...field}
                    className="w-full"
                    mode="single"
                    disabledDates={{
                      before: datetimeStart,
                    }}
                    onChange={handleChangeDate('end', field.onChange)}
                  />
                </div>
              </Field>
            )}
          />

          <Controller
            name="reason"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel>{translations.labels.reason}</FieldLabel>

                <Textarea {...field} rows={isMobile ? 6 : 4} />
              </Field>
            )}
          />

          <Activity mode={!absenceId ? 'visible' : 'visible'}>
            <Controller
              control={form.control}
              name="files"
              render={({ field }) => (
                <Field>
                  <FieldLabel>
                    {translations.labels.attachments({ n: files.length })}
                  </FieldLabel>

                  <MultiFileUpload {...field} />
                </Field>
              )}
            />
          </Activity>
        </Activity>
      </FieldGroup>
    </FormDialog>
  )
}
